import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, type ViewDef, type ScopeDef } from "./api";

/** Macro privacy view state.
 *
 *  The active view is persisted server-side (every content query
 *  filters by it). `epoch` bumps on every switch; the app shell keys
 *  the routed page subtree on it so a switch remounts the current
 *  page and its data fetches re-run against the new server-side
 *  filter (the app uses plain fetch+useEffect, not a query cache). */
interface ViewCtx {
  views: ViewDef[];
  scopes: ScopeDef[];
  activeViewId: string | null;
  activeView: ViewDef | null;
  loading: boolean;
  epoch: number;
  setActiveView: (viewId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<ViewCtx | null>(null);

export function ViewProvider({ children }: { children: ReactNode }) {
  const [views, setViews] = useState<ViewDef[]>([]);
  const [scopes, setScopes] = useState<ScopeDef[]>([]);
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [epoch, setEpoch] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const r = await api.views();
      setViews(r.views);
      setScopes(r.scopes);
      setActiveViewId(r.activeViewId);
    } catch {
      // Non-fatal: an unreachable views API must not blank the app.
      // Leaving state empty makes the pill render its Everything
      // fallback and the rest of the app behaves as no-filter.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setActiveView = useCallback(
    async (viewId: string) => {
      if (viewId === activeViewId) return;
      const prev = activeViewId;
      setActiveViewId(viewId); // optimistic
      try {
        await api.setActiveView(viewId);
        setEpoch((e) => e + 1); // force every page to refetch
      } catch {
        setActiveViewId(prev); // revert on failure
      }
    },
    [activeViewId],
  );

  const activeView = useMemo(
    () => views.find((v) => v.viewId === activeViewId) ?? null,
    [views, activeViewId],
  );

  const value = useMemo<ViewCtx>(
    () => ({
      views,
      scopes,
      activeViewId,
      activeView,
      loading,
      epoch,
      setActiveView,
      refresh,
    }),
    [views, scopes, activeViewId, activeView, loading, epoch, setActiveView, refresh],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useView(): ViewCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useView must be used within <ViewProvider>");
  return v;
}
